/**
 * Media TanStack Query hooks.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { mediaKeys } from "@/queries/keys";
import type { IMediaTranscriptMeta, ITranscriptSegment } from "@speakai/shared";
import type { FilterListItem } from "@/features/filter/api";
import {
  getMediaList,
  getMediaDetail,
  getTranscript,
  getSourceTranscript,
  getMediaCaption,
  getTranscriptRevisions,
} from "./api";
import type { MediaListResponse, SourceTranscriptResponse, JobRevision } from "./api";

export type { FilterListItem };
export type {
  JobRevision,
  MediaDuration,
  MediaSentimentDocument,
  MediaCallDetails,
  SourceTranscriptReason,
  SourceTranscriptUnavailable,
  TranslationSourceSummary,
  TranslationSourceHint,
  SourceTranscriptPayload,
  SourceTranscriptResponse,
  MediaItem,
  MediaListResponse,
  MediaDetail,
} from "./api";

export interface MediaListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  query?: string;
  filterType?: string;
  sortBy?: string;
  sortDir?: string;
  from?: string;
  to?: string;
  folderId?: string;
  isFavorites?: boolean;
  filterList?: FilterListItem[];
  enabled?: boolean;
}

// -- Query Hooks -----------------------------------------------------

/**
 * Fetch paginated media list with optional filters.
 * When filterList is provided, uses POST /media/library endpoint.
 * Otherwise uses GET /media endpoint.
 */
export function useMediaList(filters: MediaListFilters = {}) {
  const {
    page = 0,
    pageSize = 25,
    status = "2",
    query = "",
    filterType,
    sortBy = "createdAt",
    sortDir = "desc",
    from,
    to,
    folderId,
    isFavorites,
    filterList,
    enabled,
  } = filters;

  return useQuery({
    queryKey: mediaKeys.list({
      page,
      pageSize,
      status,
      query,
      filterType,
      sortBy,
      sortDir,
      from,
      to,
      folderId,
      isFavorites,
      filterList,
    }),
    queryFn: async () => {
      const res: { success: boolean; data?: MediaListResponse; error?: string } =
        await getMediaList({
          page,
          pageSize,
          status,
          query,
          filterType,
          sortBy,
          sortDir,
          from,
          to,
          folderId,
          isFavorites,
          filterList,
        });
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch media list");
      return res.data!;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMediaDetail(id: string | undefined) {
  return useQuery({
    queryKey: mediaKeys.detail(id!),
    queryFn: async () => {
      const res = await getMediaDetail(id!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch media detail");
      return res.data!;
    },
    enabled: !!id,
    staleTime: 0,
  });
}

/** Fetch in-app transcript segments for a media item. */
export interface TranscriptData extends IMediaTranscriptMeta {
  transcript: ITranscriptSegment[];
}

const selectTranscript = (data: TranscriptData) => data.transcript;
const selectTranscriptRevision = (data: TranscriptData) => data.transcriptRevision;

function transcriptQueryOptions(mediaId: string, enabled: boolean) {
  return {
    queryKey: mediaKeys.transcript(mediaId),
    queryFn: async (): Promise<TranscriptData> => {
      const res = await getTranscript(mediaId);
      return {
        transcript: res.data?.insight?.transcript ?? [],
        transcriptRevision: res.data?.transcriptRevision ?? 0,
      };
    },
    enabled: !!mediaId && enabled,
  };
}

export function useTranscript(mediaId: string, enabled = true) {
  return useQuery({ ...transcriptQueryOptions(mediaId, enabled), select: selectTranscript });
}

export function useTranscriptRevision(mediaId: string, enabled = true) {
  return useQuery({ ...transcriptQueryOptions(mediaId, enabled), select: selectTranscriptRevision });
}

/** Left disabled until the reader switches, so a translated file costs nothing extra to open. */
export function useSourceTranscript(mediaId: string, enabled = true) {
  return useQuery({
    queryKey: mediaKeys.transcriptSource(mediaId),
    queryFn: async (): Promise<SourceTranscriptResponse> => {
      const res = await getSourceTranscript(mediaId);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch the source transcript");
      return res.data!;
    },
    enabled: !!mediaId && enabled,
  });
}

/**
 * Fetch the WebVTT caption ownerId for a media item.
 *
 * Server `GET /media/caption/:mediaId` (re)generates the `.vtt` file at
 * `public/caption/<ownerUserId>/<mediaId>/<mediaId>.vtt` and returns
 * `{ userId }` in the standard success envelope. Consumers use that userId
 * to build the absolute caption URL:
 * `${serverDomain}/caption/${ownerUserId}/${mediaId}/${mediaId}.vtt`.
 *
 * Currently no React UI consumes this — the React media player has not
 * wired up WebVTT track loading yet. Exposing the hook + raw URL helper
 * so the player can use it without re-deriving the contract.
 */
export function useMediaCaption(mediaId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: mediaKeys.caption(mediaId ?? ""),
    queryFn: async () => {
      const res = await getMediaCaption(mediaId);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch media caption");
      return res.data!.userId;
    },
    enabled: !!mediaId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTranscriptRevisions(mediaId: string) {
  return useQuery({
    queryKey: mediaKeys.transcriptRevisions(mediaId),
    queryFn: async (): Promise<JobRevision[]> => {
      const res = await getTranscriptRevisions(mediaId);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch transcript revisions");
      return res.data!;
    },
    enabled: !!mediaId,
  });
}
